export type CareerScoutPaneContentType = "viewjob" | "resume_builder";

export type CareerScoutOpenPanePayload = {
  contentType: CareerScoutPaneContentType;
  resourceKey: string;
  contentUrl: string;
  source: string;
  analyticsContext?: Record<string, string>;
};

export type CareerScoutOpenViewJobPayload = {
  jobKey: string;
  viewJobUrl: string;
  from: string;
};

export type CareerScoutSelectedPane = {
  contentType: CareerScoutPaneContentType;
  resourceKey: string;
  contentUrl: string;
} | null;

export type CareerScoutPaneMode = "desktop" | "mobile";

export const CAREER_SCOUT_PANE_EVENTS = {
  selectedJobChanged: "careerScout:selectedJobChanged",
  desktopSidePaneChanged: "careerScout:desktopSidePaneChanged",
  selectedPaneChanged: "careerScout:selectedPaneChanged",
  paneEnabledChanged: "careerScout:paneEnabledChanged",
  paneModeChanged: "careerScout:paneModeChanged",
  paneOpenFailed: "careerScout:paneOpenFailed",
} as const;

/**
 * Set right before an in-flight pane mount fails. Cleared at the start of
 * every non-reused pane open attempt (see `openPane`'s `isAlreadyOpen`
 * check), not only after a successful one — a second attempt that fails
 * again clears and immediately re-sets it.
 */
export type CareerScoutPaneOpenError = {
  contentType: CareerScoutPaneContentType;
  resourceKey: string;
} | null;

export const CAREER_SCOUT_PANE_DOM = {
  desktopMediaQuery: "(min-width: 1024px)",
  mainId: "career-scout-main",
  paneId: "career-scout-pane",
  paneContentOwnsCloseClass: "career-scout-pane--content-owns-close",
  legacyPaneId: "career-scout-viewjob-pane",
  paneContentId: "career-scout-pane-content",
  legacyPaneContentId: "career-scout-viewjob-pane-content",
  paneCloseId: "career-scout-pane-close",
  legacyPaneCloseId: "career-scout-viewjob-pane-close",
  paneBackdropId: "career-scout-pane-backdrop",
  mainOpenClass: "career-scout-main--pane-open",
} as const;

export const CAREER_SCOUT_DEFAULT_VIEWJOB_FROM = "career_scout_chat";

/**
 * ViewJob `from` for Career Scout web, kept distinct from the mobile value so
 * web and mobile ViewJob traffic can be told apart. Maps to the same
 * `indeed-ace-chatbot` placement in `getJobDataInput`.
 */
export const CAREER_SCOUT_WEB_VIEWJOB_FROM = "career_scout_chat_web";

export function isCareerScoutPaneEnabled(): boolean {
  return (
    window.__CAREER_SCOUT_PANE_ENABLED__ === true ||
    window.__CAREER_SCOUT_DESKTOP_SIDE_PANE_ENABLED__ === true
  );
}

export function getCareerScoutSelectedJobKey(): string | null {
  return window.__CAREER_SCOUT_SELECTED_JOB_KEY__ ?? null;
}

export function getCareerScoutPaneOpenError(): CareerScoutPaneOpenError {
  return window.__CAREER_SCOUT_PANE_OPEN_ERROR__ ?? null;
}

/** A View Job starter pressed while the Career Scout pane is already open. */
export type CareerScoutViewjobStarterPayload = {
  starterMessage: string;
  sourceContext: string;
};

declare global {
  interface Window {
    __CAREER_SCOUT_OPEN_PANE__?: (payload: CareerScoutOpenPanePayload) => boolean | void;
    __CAREER_SCOUT_OPEN_VIEWJOB__?: (payload: CareerScoutOpenViewJobPayload) => boolean | void;
    __CAREER_SCOUT_SEND_VIEWJOB_STARTER__?: (payload: CareerScoutViewjobStarterPayload) => void;
    __CAREER_SCOUT_CLOSE_PANE__?: () => void;
    __CAREER_SCOUT_SELECTED_PANE__?: CareerScoutSelectedPane;
    __CAREER_SCOUT_PANE_ENABLED__?: boolean;
    __CAREER_SCOUT_PANE_MODE__?: CareerScoutPaneMode;
    __CAREER_SCOUT_SELECTED_JOB_KEY__?: string | null;
    __CAREER_SCOUT_DESKTOP_SIDE_PANE_ENABLED__?: boolean;
    __CAREER_SCOUT_PANE_OPEN_ERROR__?: CareerScoutPaneOpenError;
  }
}
